<script setup lang="ts">
import { computed } from 'vue'

import { useReportSchedules } from '../../composables/useReportSchedules'
import type { ReportSectionTabConfig, SectionDropdown } from '../../models/layoutConfig.types'
import ReportToolbarSelect from '../controls/ReportToolbarSelect.vue'

import { useI18n } from '@/shared/i18n'
import { type SelectOption } from '@/shared/ui'

// v3 OrphexConfigTabDropdowns (29 ln): config-declared selects whose choices become report
// filters. Options may gate themselves on schedule-tab availability (v3 `checkScheduleTabs`).

const props = defineProps<{
  tab: ReportSectionTabConfig
  /** Selected option index per dropdown (owned by `useSectionSelections`). */
  selection: (dropdown: number) => number
}>()
const emit = defineEmits<{ select: [dropdown: number, option: number] }>()
const { t } = useI18n()
const schedules = useReportSchedules()

interface RenderedDropdown {
  index: number
  dropdown: SectionDropdown
  options: SelectOption<number>[]
}

const rendered = computed<RenderedDropdown[]>(() =>
  (props.tab.dropdowns ?? []).map((dropdown, index) => ({
    index,
    dropdown,
    options: dropdown.options.flatMap((option, optionIndex) => {
      const available =
        !option.tabs?.length ||
        option.reportId === undefined ||
        Boolean(schedules.checkScheduleTabs(option.reportId, option.tabs))
      return available ? [{ label: t(option.labelKey), value: optionIndex }] : []
    }),
  })),
)
</script>

<template>
  <ReportToolbarSelect
    v-for="entry in rendered"
    :key="entry.index"
    :label="t(entry.dropdown.labelKey)"
    :options="entry.options"
    :model-value="selection(entry.index)"
    icon="circle-nodes"
    @update:model-value="(value) => emit('select', entry.index, Number(value))"
  />
</template>
